import { useEffect, useMemo, useRef } from 'react';
import { useStore } from '@tanstack/react-form';
import { useGetLookupQuery } from '../grid/api/gridApi';
import type { DetailFormJsMethodProps } from './registry';
import {
  parseInvoiceBankJsMethodOptions,
  resolveDefaultInvoiceBankId,
  resolveInvoiceCompanyFieldName,
  resolveInvoiceCurrencyFieldName,
} from './invoiceBankOptions';
import { useJsMethodLoading } from './jsMethodLoadingContext';

const normalizeObservedValue = (value: unknown): string | null => {
  if (value == null || value === '') {
    return null;
  }

  return String(value);
};

const getLookupItemLabel = (
  items: { Id: string; DisplayText?: string; Fields: Array<{ StringValue: string }> }[],
  lookupMap: Record<string, string> | undefined,
  id: unknown
) => {
  const normalizedId = String(id);
  const item = items.find((candidate) => String(candidate.Id) === normalizedId);

  return (
    lookupMap?.[normalizedId] ?? item?.DisplayText ?? item?.Fields[0]?.StringValue ?? 'Company'
  );
};

export const InvoiceBankJsMethodBinding = ({
  field,
  fields,
  form,
  contentKey,
  onNotify,
}: DetailFormJsMethodProps) => {
  const { setLoading } = useJsMethodLoading();
  const loadingKey = `${field.key ?? field.dataField ?? field.field}:${field.nameJsMethod}`;
  const options = useMemo(
    () => parseInvoiceBankJsMethodOptions(field.jsMethodOptions),
    [field.jsMethodOptions]
  );
  const observerFieldName = useMemo(
    () => (options ? resolveInvoiceCurrencyFieldName(fields, options.observerIdElement) : null),
    [fields, options]
  );
  const companyFieldName = useMemo(
    () => (options ? resolveInvoiceCompanyFieldName(fields, options.companyIdElement) : null),
    [fields, options]
  );
  const observedValue = useStore(form.store, (state) => {
    const values = (state as { values?: Record<string, unknown> }).values;
    return observerFieldName ? values?.[observerFieldName] : undefined;
  });
  const observerIsDirty = useStore(form.store, (state) => {
    const fieldMeta = (state as { fieldMeta?: Record<string, { isDirty?: boolean }> }).fieldMeta;
    return observerFieldName ? fieldMeta?.[observerFieldName]?.isDirty === true : false;
  });
  const companyField = useMemo(
    () => fields.find((candidate) => candidate.dataField === companyFieldName),
    [companyFieldName, fields]
  );
  const companyId = useStore(form.store, (state) => {
    const values = (state as { values?: Record<string, unknown> }).values;
    return companyField?.dataField ? values?.[companyField.dataField] : undefined;
  });
  const bankLookup = useGetLookupQuery(
    {
      nodeKey: contentKey ?? '',
      fieldKey: field.key ?? '',
      displayFieldName: field.additionalData?.displayExpr,
      defaultFilters: field.defaultFilters,
      selectedEntity: normalizeObservedValue(observedValue) ?? undefined,
    },
    {
      skip:
        !contentKey || !field.key || !observerFieldName || !normalizeObservedValue(observedValue),
    }
  );
  const companyLookup = useGetLookupQuery(
    {
      nodeKey: contentKey ?? '',
      fieldKey: companyField?.key ?? '',
      displayFieldName: companyField?.additionalData?.displayExpr,
      defaultFilters: companyField?.defaultFilters,
      objectId: companyId == null || companyId === '' ? 0 : String(companyId),
    },
    { skip: !contentKey || !companyField?.key || companyId == null || companyId === '' }
  );
  const previousValue = useRef<string | null | undefined>(undefined);
  const pendingLookupValue = useRef<string | null>(null);

  useEffect(() => {
    previousValue.current = undefined;
  }, [observerFieldName]);

  useEffect(() => {
    if (!options || !observerFieldName) {
      return;
    }

    const currentValue = normalizeObservedValue(observedValue);
    if (!observerIsDirty || previousValue.current === undefined) {
      previousValue.current = currentValue;
      return;
    }

    if (previousValue.current === currentValue) {
      return;
    }

    previousValue.current = currentValue;
    pendingLookupValue.current = currentValue;
    if (field.dataField) {
      form.setFieldValue(field.dataField, null);
    }
    setLoading(loadingKey, false);
  }, [
    field.dataField,
    form,
    loadingKey,
    observedValue,
    observerFieldName,
    observerIsDirty,
    options,
    setLoading,
  ]);

  useEffect(() => {
    if (
      pendingLookupValue.current === null ||
      bankLookup.isFetching ||
      !bankLookup.currentData ||
      (bankLookup.currentData.array.length === 0 && companyLookup.isFetching)
    ) {
      return;
    }

    const defaultBankId = resolveDefaultInvoiceBankId(bankLookup.currentData.array);
    if (defaultBankId && field.dataField) {
      form.setFieldValue(field.dataField, defaultBankId);
    } else if (bankLookup.currentData.array.length === 0) {
      onNotify({
        severity: 'warning',
        message: `${getLookupItemLabel(
          companyLookup.currentData?.array ?? [],
          companyLookup.currentData?.map,
          companyId
        )} doesn't have a bank account in the invoice currency.`,
      });
    }

    pendingLookupValue.current = null;
  }, [
    bankLookup.currentData,
    bankLookup.isFetching,
    companyId,
    companyLookup.currentData?.map,
    companyLookup.currentData?.array,
    companyLookup.isFetching,
    field.dataField,
    form,
    onNotify,
  ]);

  return null;
};
